import { defineStore } from 'pinia'
import { useRequestFactory } from './factories/requestFactory'
import { JupiterService } from '@/services/jupiter-service'
import { ref } from 'vue'
import type {
  JupTokenSearchResponse,
  JupErrorResponse,
  JupOrderResponse,
} from '@/services/jupiter-service/types'

export class JupError extends Error {
  public readonly response?: JupErrorResponse

  constructor(response: JupErrorResponse) {
    super(`swap error: ${response}`)
    this.response = response
    Object.setPrototypeOf(this, JupError.prototype)
  }
}

export const useJupStore = defineStore('jupiter-swap', () => {
  const { loading, errors, requestHandler } = useRequestFactory<string>()

  const orderCache = ref<Record<string, JupOrderResponse>>({})
  const orderCacheTs = ref<Record<string, number>>({})

  const cachedToken = ref<Record<string, JupTokenSearchResponse>>({})
  const apiKey = ref('')

  const execute = async (tx: string, rid: string) => {
    return await requestHandler(`execute/${rid}`, async () => {
      const req = await JupiterService.execute(apiKey.value, tx, rid)
      return req.data
    })
  }

  const getBalances = async (address: string) => {
    return await requestHandler(`getBalances/${apiKey.value}/${address}`, async () => {
      const req = await JupiterService.getBalances(apiKey.value, address)
      return req.data
    })
  }

  const getOrder = async (
    input: string,
    output: string,
    amount: number | bigint,
    taker: string,
    dexes?: string,
    slippageBps?: number,
  ) => {
    const actionId = `getOrder/${apiKey.value}/${input}/${output}/${amount}/${taker}/${dexes ?? ''}/${slippageBps ?? ''}`.toLowerCase()

    const dt = +new Date()
    if (dt - (orderCacheTs.value[actionId] ?? 0) <= 3000 && actionId in orderCache.value) {
      console.log(`got cached transaction ${actionId}`)
      return orderCache.value[actionId]
    }

    return await requestHandler(actionId, async () => {
      const req = await JupiterService.getOrder(
        apiKey.value,
        input,
        output,
        amount.toString(),
        taker,
        dexes,
        slippageBps,
      )
      return req.data
    })
  }

  const search = async (token: string) => {
    if (cachedToken.value[token]) {
      return cachedToken.value[token]
    }
    const actionId = token
    return await requestHandler(actionId, async () => {
      const req = await JupiterService.search(apiKey.value, token)
      if (!('error' in req.data)) {
        cachedToken.value[token] = req.data as JupTokenSearchResponse
        return req.data
      } else {
        throw new JupError(req.data as JupErrorResponse)
      }
    })
  }

  return {
    apiKey,
    loading,
    errors,
    execute,
    getBalances,
    getOrder,
    search,
  }
})
